import { useCallback, useState } from 'react';
import dayjs from 'dayjs';

import { VALIDATORS } from '@/common';
import { DATE_INVALID_ALTERNATIVE_TEXT, DEFAULT_DATE_FORMAT } from '@/common/constants';
import { useMyMoneyFormStore } from '@/modules/UseMyMoney/stores';

const isDateInFuture = (date?: string) => dayjs(date).valueOf() > dayjs().valueOf();

interface Props {
  dateOfService: string;
  setDateOfService: (value: string) => void;
  isEditModeByDefault: boolean;
}

const useChangeDateOfService = ({ dateOfService, setDateOfService, isEditModeByDefault }: Props) => {
  const { handleSetFormValues } = useMyMoneyFormStore();
  const [errorText, setErrorText] = useState('');
  const [isEditMode, setIsEditMode] = useState(isEditModeByDefault);

  const handleValidateDate = useCallback((newDateOfService: string) => {
    try {
      VALIDATORS.DATE_FORMAT.test(
        'date',
        DATE_INVALID_ALTERNATIVE_TEXT,
        (date) => !isDateInFuture(date)
      ).validateSync(newDateOfService);
      setErrorText('');
    } catch (error) {
      setErrorText(DATE_INVALID_ALTERNATIVE_TEXT);
      throw error;
    }
  }, []);

    const handleChangeDateInput = useCallback((newValue: string) => {
    setDateOfService(newValue);
    if (newValue?.length === DEFAULT_DATE_FORMAT.length) {
      handleValidateDate(newValue);
    } else {
      setErrorText('');
    }
  }, [handleValidateDate, setDateOfService]);

  const handleChangeSuggestedDateOfService = useCallback((newDateOfService: string) => {
    handleValidateDate(newDateOfService);
    handleSetFormValues({ dateOfService: newDateOfService });
    setIsEditMode(false);
  }, [handleSetFormValues, handleValidateDate]);

  const handleChangeDateOfService = useCallback((newDateOfService: string) => {
    handleValidateDate(newDateOfService);
    handleSetFormValues({ dateOfService: newDateOfService, customDateOfService: newDateOfService });
    setIsEditMode(false);
  }, [handleSetFormValues, handleValidateDate]);

  const handleSave = useCallback(() => {
    handleValidateDate(dateOfService);
    handleSetFormValues({ dateOfService, customDateOfService: dateOfService });
    setIsEditMode(false);
  }, [handleSetFormValues, handleValidateDate, dateOfService]);

  const handleCancel = useCallback(() => {
    handleSetFormValues({ dateOfService: '', customDateOfService: '' });
    setDateOfService('');
  }, [handleSetFormValues, setDateOfService]);

  return {
    isEditMode,
    dateOfService,
    errorText,
    setIsEditMode,
    handleChangeDateInput,
    handleChangeSuggestedDateOfService,
    handleChangeDateOfService,
    handleSave,
    handleCancel,
  };
};

export default useChangeDateOfService;
